import { useEffect, useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { toast } from 'react-toastify';
import { formatDistanceToNow } from 'date-fns';
import { useMessageHubStore } from '../../store/messageHubStore';
import { MessageHubShell } from './MessageHubShell';
import { MESSAGE_HUB_PAGE_SIZE } from './MessageHubListPagination';
import {
  MessageHubListColumn,
  MessageHubDetailPanel,
  MessageHubEmptyDetail,
  MessageHubListItem,
  HubAvatar,
  HubStatusBadge,
} from './MessageHubShared';
import { parseHubRecruiterDisplay } from './messageHubRecruiter';

interface Recruiter {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profileImg: string | null;
  company: string | null;
}

interface Contact {
  id: string;
  establishedAt: string;
  recruiter: Recruiter;
  lastMessage: string;
  lastMessageAt: string | null;
  status?: string;
}

export default function ContactsFlow() {
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');
  const [search, setSearch] = useState('');
  const [listPage, setListPage] = useState(1);
  const contacts = useMessageHubStore((state) => state.contacts) as Contact[];
  const loading = useMessageHubStore((state) => state.contactsLoading);
  const fetchContacts = useMessageHubStore((state) => state.fetchContacts);
  const setContacts = useMessageHubStore((state) => state.setContacts);
  const navigate = useNavigate();

  const selected = contacts.find((c) => c.id === selectedContact);

  useEffect(() => {
    void fetchContacts().catch(console.error);
  }, [fetchContacts]);

  useEffect(() => {
    setListPage(1);
  }, [search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      const { name, company } = parseHubRecruiterDisplay(c.recruiter);
      return (
        name.toLowerCase().includes(q) ||
        company.toLowerCase().includes(q) ||
        String(c.recruiter.email || '')
          .toLowerCase()
          .includes(q)
      );
    });
  }, [contacts, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / MESSAGE_HUB_PAGE_SIZE));
  const safeListPage = Math.min(listPage, totalPages);
  const pagedFiltered = useMemo(
    () =>
      filtered.slice(
        (safeListPage - 1) * MESSAGE_HUB_PAGE_SIZE,
        safeListPage * MESSAGE_HUB_PAGE_SIZE
      ),
    [filtered, safeListPage]
  );

  const handleStatusUpdate = async (newStatus: string) => {
    if (!selected?.id) return;

    try {
      await axiosInstance.put(`${API_ROUTES.MESSENGER.UPDATE_CONTACT_STATUS}/${selected.id}`, {
        status: newStatus,
      });

      setContacts(contacts.map((c) => (c.id === selected.id ? { ...c, status: newStatus } : c)));

      toast.success(`Status updated to ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const listColumn = (
    <MessageHubListColumn
      title="Contacts"
      searchPlaceholder="Search contacts…"
      search={search}
      onSearchChange={setSearch}
      loading={loading}
      loadingLabel="Contacts"
      emptyMessage="No contacts found."
      listPage={safeListPage}
      totalItems={filtered.length}
      onPageChange={setListPage}
    >
      {pagedFiltered.map((c) => {
        const { name, company } = parseHubRecruiterDisplay(c.recruiter);
        const connected = formatDistanceToNow(new Date(c.establishedAt), {
          addSuffix: true,
        });
        return (
          <MessageHubListItem
            key={c.id}
            active={selectedContact === c.id}
            onClick={() => {
              setSelectedContact(c.id);
              setMobileView('detail');
            }}
            avatar={<HubAvatar name={name} />}
            name={name}
            meta={company || '—'}
            preview={`Connected ${connected}`}
            badges={c.status ? <HubStatusBadge status={c.status} variant="neutral" /> : undefined}
          />
        );
      })}
    </MessageHubListColumn>
  );

  const selectedDisplay = selected
    ? parseHubRecruiterDisplay(selected.recruiter)
    : { name: '', company: '' };

  const detailColumn = selected ? (
    <MessageHubDetailPanel
      onBack={() => setMobileView('list')}
      title={selectedDisplay.name}
      subtitle={selectedDisplay.company || '—'}
      avatar={<HubAvatar name={selectedDisplay.name} large />}
      footer={
        <div className="mh-detail-actions">
          <button
            type="button"
            className="mh-btn mh-btn--primary"
            onClick={() => navigate('/messages')}
          >
            Message
          </button>
          <button
            type="button"
            className="mh-btn mh-btn--secondary"
            onClick={() => void handleStatusUpdate('Muted')}
          >
            Mute
          </button>
          <button
            type="button"
            className="mh-btn mh-btn--danger"
            onClick={() => void handleStatusUpdate('Blocked')}
          >
            Block
          </button>
          <button
            type="button"
            className="mh-btn mh-btn--outline"
            onClick={() => void handleStatusUpdate('Removed')}
          >
            Remove
          </button>
        </div>
      }
    >
      <div className="mh-detail-card">
        <p className="mh-detail-card-label">Status</p>
        <HubStatusBadge status={selected.status || 'active'} variant="neutral" />
        <p className="mh-detail-card-body">
          Connected{' '}
          {formatDistanceToNow(new Date(selected.establishedAt), {
            addSuffix: true,
          })}
        </p>
        {selected.recruiter.email ? (
          <p className="mh-detail-card-muted">{selected.recruiter.email}</p>
        ) : null}
      </div>
    </MessageHubDetailPanel>
  ) : (
    <MessageHubEmptyDetail
      icon={<Users className="h-7 w-7" />}
      title="Select a contact"
      description="Pick someone from your list to view details and manage the relationship."
    />
  );

  return (
    <MessageHubShell
      active="contacts"
      mobileView={mobileView}
      layoutRevision={selectedContact ?? 'empty'}
      listColumn={listColumn}
      detailColumn={detailColumn}
    />
  );
}
